// CHG-131 REQ-CP-011: 공용 아바타
// - avatarUrl 있으면 <img>, 없으면 displayColor 배경 + 흰색 이니셜
// - displayColor 누락 시 pickColorByUserIdFallback(userId) — 마이그레이션 후 평시 미실행
// - onClick 있으면 cursor-pointer + hover:opacity-80 + button 시맨틱
import {
  getColorClasses,
  pickColorByUserIdFallback,
  type ProfileColorKey,
} from '@maios/shared';

export interface AvatarProps {
  userId: string;
  name: string;
  avatarUrl?: string | null;
  displayColor?: ProfileColorKey | null;
  size?: 'xs' | 'sm' | 'md' | 'lg'; // 24/32/40/56 px
  onClick?: () => void;
  className?: string;
  // CHG-131: 이니셜 표시용 firstName(이름). 닉네임이 비공개거나 미설정이면 firstName으로 초기화.
  // 없으면 name 첫 2글자 fallback.
  firstName?: string | null;
}

const SIZE_CLASSES: Record<NonNullable<AvatarProps['size']>, string> = {
  xs: 'size-6 text-[11px]',
  sm: 'size-8 text-sm',
  md: 'size-10 text-sm',
  lg: 'size-14 text-base',
};

function getInitials(name: string, firstName?: string | null): string {
  // CHG-131: firstName(이름) 우선. 없으면 name 첫 2글자 fallback.
  const source = firstName?.trim() || name.trim();
  if (!source) return '?';
  // 한글/영문 공통 — 첫 2글자
  return [...source].slice(0, 2).join('');
}

export default function Avatar({
  userId,
  name,
  avatarUrl,
  displayColor,
  size = 'md',
  onClick,
  className = '',
  firstName,
}: AvatarProps) {
  // displayColor 누락 시 클라이언트 fallback (djb2). 마이그레이션 이후 평시 미실행.
  const colorKey: ProfileColorKey = displayColor ?? pickColorByUserIdFallback(userId);
  const palette = getColorClasses(colorKey);

  const sizeClass = SIZE_CLASSES[size];
  const baseClass = `inline-flex items-center justify-center rounded-full shrink-0 select-none ${sizeClass}`;
  const interactClass = onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : '';

  if (avatarUrl) {
    const imgClass = `${baseClass} ${interactClass} object-cover ${className}`;
    return onClick ? (
      <button type="button" onClick={onClick} className={`${baseClass} ${interactClass} ${className}`} aria-label={`${name}의 프로필`}>
        <img src={avatarUrl} alt={name} className={`${sizeClass} rounded-full object-cover`} />
      </button>
    ) : (
      <img src={avatarUrl} alt={name} className={imgClass} />
    );
  }

  // 이니셜 fallback — firstName이 있으면 이를 우선 사용 (REQ "이름만 노출")
  const initials = getInitials(name, firstName);
  const content = (
    <span className={`font-semibold text-white leading-none`}>{initials}</span>
  );
  const fullClass = `${baseClass} ${palette.bgClass} ${interactClass} ${className}`;

  return onClick ? (
    <button type="button" onClick={onClick} className={fullClass} aria-label={`${name}의 프로필`}>
      {content}
    </button>
  ) : (
    <div className={fullClass} aria-label={name}>
      {content}
    </div>
  );
}
